import { useEffect, useRef, useState } from 'react';
import { PageMeta } from '../../components/shared/PageMeta';
import { Navigation } from '../../components/feature/Navigation';
import { FooterSection } from '../../components/feature/FooterSection';
import { MinimalAurora } from '../../components/feature/MinimalAurora';
import { CheckIntro } from './components/CheckIntro';
import { CheckQuiz, type QuizAnswer } from './components/CheckQuiz';
import { CheckResult } from './components/CheckResult';
import { WaitlistForm } from './components/WaitlistForm';
import { QUESTIONS, scoreToTier } from './data/questions';

type Stage = 'intro' | 'quiz' | 'result';

export default function CheckPage() {
  const [stage, setStage] = useState<Stage>('intro');
  const [answers, setAnswers] = useState<QuizAnswer[]>([]);
  const waitlistRef = useRef<HTMLElement>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [stage]);

  const handleStart = () => {
    setAnswers([]);
    setStage('quiz');
  };

  const handleComplete = (next: QuizAnswer[]) => {
    setAnswers(next);
    setStage('result');
  };

  const handleRetake = () => {
    setAnswers([]);
    setStage('intro');
  };

  const scrollToWaitlist = () => {
    waitlistRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const correct = answers.filter((a) => a.correct).length;
  const tier = stage === 'result' ? scoreToTier(correct, QUESTIONS.length) : undefined;

  return (
    <div className="min-h-screen relative bg-black">
      <PageMeta path="/check" />
      <MinimalAurora />
      <div className="relative z-10">
        <Navigation />
        <main>
          {stage === 'intro' && <CheckIntro onStart={handleStart} />}
          {stage === 'quiz' && <CheckQuiz onComplete={handleComplete} />}
          {stage === 'result' && (
            <CheckResult
              answers={answers}
              onRetake={handleRetake}
              onJoinWaitlist={scrollToWaitlist}
            />
          )}
          <WaitlistForm
            ref={waitlistRef}
            awarenessScore={stage === 'result' ? correct : undefined}
            awarenessTier={tier}
          />
        </main>
        <FooterSection />
      </div>
    </div>
  );
}
